<script lang="ts">
	import type { HolsterFinderPistolMapping, HolsterFinderCartridge, HolsterFinderPistolModel, HolsterFinderPistolLight } from 'endpoint/holster_line/configurator'

	import { onMount } from 'svelte'
	import { get_initial_values, sort_pistol_makes, get_families, get_cartridges, get_pistol_models, get_pistol_lights } from 'lib/pistol_mapping_helpers'
	import Label from 'shared/component/StorefrontLabel.svelte'
	import Select from 'shared/component/Select.svelte'
	import HolsterProductCategoryPage from '../holster_product_category_page/HolsterProductCategoryPage.svelte'
	import { serialize_to_address_bar, deserialize_from_address_bar } from 'shared/query_serialization'
	import remove_undefined_values from 'shared/object/remove_undefined_values'
	import assert from 'shared/assert'

	export let promo_active
	export let pistol_mapping: HolsterFinderPistolMapping
	export let holster_lines: ResourceTypes[`holster_line`][]

	const holster_lines_map = new Map(holster_lines.map(holster_line => [ Number(holster_line.id), holster_line ]))

	let pistol_families: TrexPistolFamily[] | null
	let cartridges: HolsterFinderCartridge[] | null
	let sorted_pistol_makes: TrexPistolMake[] = []

	let selected_pistol_make: TrexPistolMake | null
	let selected_pistol_family: TrexPistolFamily | null
	let selected_cartridge: HolsterFinderCartridge | null
	let selected_pistol_model_with_pistol: HolsterFinderPistolModel | null
	let selected_light: HolsterFinderPistolLight | null
	let safe_to_serialize = false

	let {
		pistol_makes,
		pistol_make_ref_to_pistol_families_with_availability,
		pistol_family_ref_to_cartridges_to_pistols,
		pistol_ref_to_lights_to_holster_availabilities,
	} = pistol_mapping

	onMount(() => {
		({
			pistol_make: selected_pistol_make,
			pistol_family: selected_pistol_family,
			cartridge: selected_cartridge,
			// @ts-expect-error Generics aren't flowing through get_intitial_values and get_default_values correctly
			pistol_model: selected_pistol_model_with_pistol,
			pistol_light: selected_light,
		} = get_initial_values({
			pistol_makes,
			pistol_make_ref_to_pistol_families_with_availability,
			pistol_family_ref_to_cartridges_to_pistols,
			pistol_ref_to_lights_to_holster_availabilities,
			...deserialize_from_address_bar(),
		}))

		safe_to_serialize = true
	})

	$: safe_to_serialize && serialize_to_address_bar(remove_undefined_values({
		pistol_make_ref: selected_pistol_make?.pistol_make_ref,
		pistol_family_ref: selected_pistol_family?.pistol_family_ref,
		cartridge_ref: selected_cartridge?.cartridge_ref,
		pistol_model_ref: selected_pistol_model_with_pistol?.pistol_model_ref,
		pistol_light_ref: selected_light?.pistol_light_ref,
	}))

	const find_matching_item_by_property = <T extends object, P extends keyof T>(array: T[] | null, item: T | null, property: P) => array?.find(element => element[property] === item?.[property]) || null

	const on_pistol_makes = () => {
		selected_pistol_make = find_matching_item_by_property(pistol_makes, selected_pistol_make, `pistol_make_ref`) || null
		sorted_pistol_makes = sort_pistol_makes(pistol_makes)
	}

	$: pistol_makes && on_pistol_makes()

	$: if (pistol_make_ref_to_pistol_families_with_availability) {
		pistol_families = get_families({
			pistol_make: selected_pistol_make,
			pistol_make_ref_to_pistol_families_with_availability,
		})
	}

	const exactly_one = (options: unknown[] | null) => options?.length === 1

	const on_pistol_families = (pistol_families: TrexPistolFamily[] | null) => {
		selected_pistol_family = find_matching_item_by_property(pistol_families, selected_pistol_family, `pistol_family_ref`)
			|| (pistol_families && exactly_one(pistol_families) ? pistol_families[0] : null)
	}
	$: on_pistol_families(pistol_families)

	$: if (pistol_family_ref_to_cartridges_to_pistols) {
		cartridges = get_cartridges({
			pistol_family: selected_pistol_family,
			pistol_family_ref_to_cartridges_to_pistols,
		})
	}

	const on_cartridges = (cartridges: HolsterFinderCartridge[] | null) => {
		selected_cartridge = find_matching_item_by_property(cartridges, selected_cartridge, `cartridge_ref`)
			|| (cartridges && exactly_one(cartridges) ? cartridges[0] : null)
	}
	$: on_cartridges(cartridges)

	$: pistol_models = get_pistol_models(selected_cartridge)

	const on_pistol_models = (pistol_models: HolsterFinderPistolModel[] | null) => {
		const matching_model = find_matching_item_by_property(pistol_models, selected_pistol_model_with_pistol, `pistol_model_ref`)

		selected_pistol_model_with_pistol = matching_model || (pistol_models && exactly_one(pistol_models) ? pistol_models[0] : null)
	}

	$: on_pistol_models(pistol_models)

	$: pistol_lights = get_pistol_lights({
		pistol: selected_pistol_model_with_pistol?.pistol || null,
		pistol_ref_to_lights_to_holster_availabilities,
	})

	const on_pistol_lights = (pistol_lights: HolsterFinderPistolLight[] | null) => {
		selected_light = find_matching_item_by_property(pistol_lights, selected_light, `pistol_light_ref`) || (pistol_lights && exactly_one(pistol_lights) ? pistol_lights?.[0] : null)
	}

	$: on_pistol_lights(pistol_lights)

	$: available_holsters = selected_light ? selected_light.holster_availabilities.map(({ holster_line_ref, holster_availability_ref }) => {
		const holster_line = holster_lines_map.get(holster_line_ref)
		assert(holster_line)
		return {
			...holster_line,
			holster_availability_ref,
		}
	}).sort((a, b) => a.attributes.storefront_sort_order - b.attributes.storefront_sort_order) : []

	// lull the customer into a belief that work is actually being done
	$: fake_delay_available_holsters_promise = new Promise<typeof available_holsters>(resolve => setTimeout(() => resolve(available_holsters), 100))
</script>

<div class="container">
	<h2>Holster Finder</h2>
	<div class=holster_finder>
		<div class="group">
			<Label for="make-input">
				Make
			</Label>
			<div class="input">
				{#if exactly_one(pistol_makes)}
					<div class="single_value">
						{pistol_makes[0].name}
					</div>
				{:else if pistol_makes}
					<Select
						bind:value={selected_pistol_make}
						id=make-input
						placeholder='Select a make'
						autofocus
					>
						{#each sorted_pistol_makes as make (make.pistol_make_ref)}
							<option value={make}>{make.name}</option>
						{/each}
					</Select>
				{:else}
					<Select disabled></Select>
				{/if}
			</div>
		</div>
		<div class="group">
			<Label for="family-input">
				{selected_pistol_make?.name_for_family || `Family`}
			</Label>
			<div class="input">
				{#if exactly_one(pistol_families)}
					<div class="single_value">
						{pistol_families?.[0].name}
					</div>
				{:else if pistol_families}
					<Select
						bind:value={selected_pistol_family}
						id=family-input
						placeholder='Select a family'
					>
						{#each pistol_families as family (family.pistol_family_ref)}
							<option value={family}>{family.name}</option>
						{/each}
					</Select>
				{:else}
					<Select disabled></Select>
				{/if}
			</div>
		</div>
		<div class="group">
			<Label for="cartridge-input">
				Caliber
			</Label>
			<div class="input">
				{#if exactly_one(cartridges)}
					<div class="single_value">
						{cartridges![0].name}
					</div>
				{:else if cartridges}
					<Select
						bind:value={selected_cartridge}
						id=cartridge-input
						placeholder='Select a caliber'
					>
						{#each cartridges as cartridge (cartridge.cartridge_ref)}
							<option value={cartridge}>{cartridge.name}</option>
						{/each}
					</Select>
				{:else}
					<Select disabled></Select>
				{/if}
			</div>
		</div>
		<div class="group">
			<Label for="model-input">
				Model
			</Label>
			<div class="input">
				{#if exactly_one(pistol_models)}
					<div class="single_value">
						{pistol_models?.[0].name}
					</div>
				{:else if pistol_models}
					<Select
						bind:value={selected_pistol_model_with_pistol}
						id=model-input
						placeholder='Select a model'
					>
						{#each pistol_models as model (model.pistol_model_ref)}
							<option value={model}>{model.name}</option>
						{/each}
					</Select>
				{:else}
					<Select disabled></Select>
				{/if}
			</div>
		</div>
		<div class="group">
			<Label for="weaponlight-input">
				Weapon Light
			</Label>
			<div class="input">
				{#if exactly_one(pistol_lights)}
					<div class="single_value">
						{pistol_lights?.[0].name}
					</div>
				{:else if pistol_lights?.length}
					<Select
						bind:value={selected_light}
						id=weaponlight-input
						placeholder='Select a light'
					>
						{#each pistol_lights as light (light.pistol_light_ref)}
							<option value={light}>{light.name}</option>
						{/each}
					</Select>
				{:else}
					<Select disabled></Select>
				{/if}
			</div>
		</div>
	</div>
	{#if available_holsters.length}
		{#await fake_delay_available_holsters_promise}
			<div class="spinner"></div>
		{:then available_holsters}
			<div class=found_some>We make { available_holsters.length } { exactly_one(available_holsters) ? `holster that fits` : `holsters that fit` } your weapon.</div>
			<HolsterProductCategoryPage
				holster_lines={available_holsters}
				{promo_active}
			/>
		{/await}
	{/if}
</div>

<style>
	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	.holster_finder {
		--contrast-text: #fff;
		--contrast-gray: #b3b3b3;
		--disabled-gray: #474747;
		--input-border-gray: #666; /* woocommerce dark.css has the same color */
		--trex-green: #CEFF51;
		--better-contrast-background: #333;
		--attention-red: #f00;
		display: grid;
		gap: 20px;
		align-items: start;
		grid-template-columns: repeat(5, 1fr);
	}

	h2 {
		text-align: center;
	}

	.group {
		display: grid;
		grid-template-rows: auto 1fr;
		gap: 5px 0;
	}

	.holster_finder :global(label) {
		grid-row: 1;
	}

	.input {
		grid-row: 2;
	}

	@media (max-width: 800px) {
		.holster_finder {
			grid-template-columns: auto;
		}
		.group {
			grid-template-columns: 90px 1fr;
			align-items: center;
			gap: 0 10px;
		}

		.holster_finder :global(label) {
			grid-row: auto;
			grid-column: 1;
		}

		.input {
			grid-row: auto;
			grid-column: 2;
			width: 100%;
		}
	}

	.single_value {
		font-size: 16px;
		line-height: 40px;
	}

	.spinner {
		margin: 20px auto;
		width: 100px;
		height: 100px;
		background: black url('https://img.trex-arms.com/storefront/loading_100.gif');
		background-blend-mode: difference;
		border-radius: 100%;
		border: 0;
		pointer-events: none;
	}

	.found_some {
		text-align: center;
	}
</style>
